import { createSessionStoredValue } from "./storage/createSessionStoredValue.ts";

const CONSENT_ORIGIN_SESSION_KEY = "sg-consent-origin";
const CONSENT_RETURN_TO_SESSION_KEY = "sg-consent-return-to";
const SETUP_PREVIOUS_STEP_SESSION_KEY = "sg-setup-previous-step";
const PENDING_PROJECT_CONSENT_SESSION_KEY = "sg-pending-project-consent";

// The BFF reads these cookies by name: DecryptWorkspaceTenant{Consent,Reconsent}RequestFilter.TokenCookieName.
export const CONSENT_REQUEST_TOKEN_COOKIE = "workspace-tenant-consent-request-token";
export const RECONSENT_REQUEST_TOKEN_COOKIE = "workspace-tenant-reconsent-request-token";

export const consentOriginStorage = createSessionStoredValue(CONSENT_ORIGIN_SESSION_KEY);
export const consentReturnToStorage = createSessionStoredValue(CONSENT_RETURN_TO_SESSION_KEY);

const setupPreviousStepStore = createSessionStoredValue(SETUP_PREVIOUS_STEP_SESSION_KEY);

function readSetupPreviousStepStack(): string[] {
    const raw = setupPreviousStepStore.get();

    if (!raw) {
        return [];
    }

    try {
        const parsed: unknown = JSON.parse(raw);

        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [raw];
    }
}

export const setupPreviousStepStorage = {
    push(path: string): void {
        const stack = readSetupPreviousStepStack();

        stack.push(path);
        setupPreviousStepStore.set(JSON.stringify(stack));
    },
    pop(): string | null {
        const stack = readSetupPreviousStepStack();
        const path = stack.pop() ?? null;

        setupPreviousStepStore.set(stack.length > 0 ? JSON.stringify(stack) : null);

        return path;
    },
    clear(): void {
        setupPreviousStepStore.set(null);
    }
};

export interface PendingProjectConsent {
    projectId: string;
    tenantId?: string;
}

function isPendingProjectConsent(value: unknown): value is PendingProjectConsent {
    if (typeof value !== "object" || value === null) {
        return false;
    }

    if (!("projectId" in value) || typeof value.projectId !== "string") {
        return false;
    }

    return !("tenantId" in value) || typeof value.tenantId === "string";
}

const pendingProjectConsentStore = createSessionStoredValue(PENDING_PROJECT_CONSENT_SESSION_KEY);

function readPendingProjectConsent(): PendingProjectConsent | null {
    const raw = pendingProjectConsentStore.get();

    if (!raw) {
        return null;
    }

    try {
        const parsed: unknown = JSON.parse(raw);

        return isPendingProjectConsent(parsed) ? parsed : null;
    } catch {
        return null;
    }
}

export const pendingProjectConsentStorage = {
    get(): PendingProjectConsent | null {
        return readPendingProjectConsent();
    },
    setProject(projectId: string): void {
        pendingProjectConsentStore.set(JSON.stringify({ projectId } satisfies PendingProjectConsent));
    },
    setTenantId(tenantId: string): void {
        const current = readPendingProjectConsent();

        if (!current) {
            return;
        }

        pendingProjectConsentStore.set(JSON.stringify({ ...current, tenantId } satisfies PendingProjectConsent));
    },
    clearTenantId(): void {
        const current = readPendingProjectConsent();

        if (!current) {
            return;
        }

        pendingProjectConsentStore.set(
            JSON.stringify({ projectId: current.projectId } satisfies PendingProjectConsent)
        );
    },
    clear(): void {
        pendingProjectConsentStore.set(null);
    }
};
